import * as Sentry from '@sentry/nextjs';
import { useState } from 'react';
import useSWR from 'swr';
import dynamic from 'next/dynamic';
import { Box, Flex } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useBlog, useTranslation } from '../../lib/hooks.context';
import { DataTable } from '../DataTable';
import Heading from '../Heading';

import type { SiteOption } from '../SiteSelect';

const SiteSelect = dynamic(() => import('../SiteSelect'), { ssr: false });

type ElectionTableRow = {
  name: string;
  title: string;
  municipality: string;
  status: string;
};

const fetcher = (endpoint: string) =>
  fetch(endpoint)
    .then(async (res) => {
      if (res.ok) {
        if (res.status === 204) {
          return { results: [] };
        }
        return res.json();
      }
      // Returning null as we don't want to render anything if the fetch fails
      return null;
    })
    .catch((err) => {
      /* do nothing for now. */
      console.error('Failed to fetch: ', err);
      Sentry.captureException(err);
      return { error: 'Failed to fetch', description: err.message };
    });

const ElectionResults = () => {
  const t = useTranslation();
  const subsite = useBlog(true);
  const [site, setSite] = useState<SiteOption>({
    id: subsite.blog_id,
    name: subsite.blogname,
    value: subsite.name,
  });
  const { data } = useSWR(
    site && site.value !== '' ? `/api/elections/${site.value}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      errorRetryInterval: 0,
      shouldRetryOnError: false,
      dedupingInterval: 3600000,
    },
  );

  const columnHelper = createColumnHelper<ElectionTableRow>();

  const columns = [
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: t('name'),
    }),
    columnHelper.accessor('title', {
      cell: (info) => info.getValue(),
      header: t('job_title'),
    }),
    columnHelper.accessor('municipality', {
      cell: (info) => info.getValue(),
      header: t('municipality'),
    }),
    columnHelper.accessor('status', {
      cell: (info) => info.getValue(),
      header: t('elections_status'),
    }),
  ];

  const onSelect = (option: SiteOption) => {
    if (!option || option.value === site.name) {
      return;
    }
    setSite(option);
  };

  return (
    <Box>
      <Flex
        borderRadius="lg"
        bg="greenBirch"
        py="8"
        px={[4, null, 10, null, '10']}
        alignItems="center"
        justifyContent="space-between"
      >
        <Heading as="h2">{`${t('election_results')} ${
          site.id !== 1 ? site.name : ''
        }`}</Heading>
        <SiteSelect
          placeholder={t('search_association_placeholder')}
          onAdd={onSelect}
          autoFocus={false}
        />
      </Flex>
      {
        // eslint-disable-next-line no-nested-ternary
        data ? (
          data.results?.length ? (
            <Box mt="12">
              <DataTable columns={columns} data={data.results} />
            </Box>
          ) : (
            <Box mt="12" textAlign="center">
              {t('no_election_results')}
            </Box>
          )
        ) : null
      }
    </Box>
  );
};

export default ElectionResults;
